import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Trophy, Zap, Target, Play, LogOut, User,
  BarChart2, Clock, Swords, Sun, Moon, BookOpen,
  ChevronRight, Award, Lock, ChevronDown,
} from "lucide-react";
import { motion } from "framer-motion";
import DifficultyModal from "@/components/DifficultyModal";

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("tg-theme") === "dark"; } catch { return false; }
  });
  useEffect(() => { localStorage.setItem("tg-theme", dark ? "dark" : "light"); }, [dark]);
  return [dark, setDark];
}

const LEVEL_INFO = [
  { min: 0,     max: 500,    name: "Bronze Tax Scholar",      emoji: "🥉", color: "#ea580c" },
  { min: 500,   max: 1500,   name: "Silver Tax Analyst",      emoji: "🥈", color: "#64748b" },
  { min: 1500,  max: 3500,   name: "Gold Tax Advisor",        emoji: "🥇", color: "#d97706" },
  { min: 3500,  max: 7000,   name: "Platinum Tax Strategist", emoji: "💎", color: "#4f46e5" },
  { min: 7000,  max: 999999, name: "Diamond Tax Master",      emoji: "👑", color: "#0891b2" },
];
const getLevel = (gp) => LEVEL_INFO.find(l => gp >= l.min && gp < l.max) || LEVEL_INFO[0];

// All quiz topics — active ones are selectable
const QUIZ_TOPICS = [
  { id: "income-tax", emoji: "📘", label: "Old Income Tax Vs New Income Tax", questions: 1000, active: true },
  { id: "gst",          emoji: "🧾", label: "GST — Goods & Services Tax",       active: false },
  { id: "tds-tcs",      emoji: "💼", label: "TDS & TCS",                         active: false },
  { id: "capital-gains",emoji: "📈", label: "Capital Gains",                     active: false },
  { id: "corporate-tax",emoji: "🏢", label: "Corporate Tax",                     active: false },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [D, setDark] = useDarkMode();
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDifficultyModal, setShowDifficultyModal] = useState(false);
  const [pendingChallenges, setPendingChallenges]     = useState(0);
  const [activeNav, setActiveNav] = useState("dashboard");
  const [selectedTopicId, setSelectedTopicId] = useState("income-tax"); // default to the active topic
  const [difficultyStatus, setDifficultyStatus] = useState({});

  useEffect(() => { fetchStats(); fetchPendingChallenges(); fetchDifficultyStatus(); }, [token]);

  const fetchPendingChallenges = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/challenge/pending`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setPendingChallenges(d.received?.length || 0); }
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setStats(d); }
    } catch {} finally { setLoading(false); }
  };

  const fetchDifficultyStatus = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz/difficulty-status`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setDifficultyStatus(d.difficulties || {}); }
    } catch {}
  };

  const handleOpenDifficultyModal = () => {
    if (selectedTopic?.active) {
      fetchDifficultyStatus();
      setShowDifficultyModal(true);
    }
  };

  const handleStartQuiz = (difficulty) => navigate("/quiz", { state: { difficulty } });
  const handleLogout    = () => { logout(); navigate("/"); };

  const selectedTopic   = QUIZ_TOPICS.find(t => t.id === selectedTopicId) || QUIZ_TOPICS[0];

  // ── Theme tokens ──────────────────────────────────────────────────────────────
  const bg       = D ? "#0f172a" : "#f0fdf4";
  const surface  = D ? "#1e293b" : "#ffffff";
  const surface2 = D ? "#253347" : "#f8fafc";
  const border   = D ? "#334155" : "#e2e8f0";
  const textPri  = D ? "#f1f5f9" : "#1e293b";
  const textSec  = D ? "#94a3b8" : "#64748b";
  const navHov   = D ? "#253347" : "#f0fdf4";
  const accentGreen = "#10b981";

  const totalGP  = stats?.total_gp || user?.total_gp || 0;
  const level    = getLevel(totalGP);
  const nextLevel= LEVEL_INFO[LEVEL_INFO.indexOf(level) + 1];
  const pct      = nextLevel ? Math.round(((totalGP - level.min) / (nextLevel.min - level.min)) * 100) : 100;

  const navItems = [
    { id: "dashboard",   label: "Dashboard",   icon: <BarChart2 size={18} />, action: () => setActiveNav("dashboard") },
    { id: "challenges",  label: "Challenges",  icon: <Swords size={18} />, badge: pendingChallenges, action: () => navigate("/challenges") },
    { id: "leaderboard", label: "Leaderboard", icon: <Trophy size={18} />, action: () => navigate("/leaderboard") },
    { id: "profile",     label: "Profile",     icon: <User size={18} />,   action: () => navigate("/profile") },
  ];

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Nunito', sans-serif", transition: "background 0.3s" }}>

      {/* ── Top Navbar ── */}
      <header style={{ position: "sticky", top: 0, zIndex: 50, background: D ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.95)", borderBottom: `1px solid ${border}`, backdropFilter: "blur(12px)", transition: "background 0.3s, border-color 0.3s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>

          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setActiveNav("dashboard")}>
            <div style={{ width: 38, height: 38, background: "linear-gradient(135deg, #059669, #0d9488)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "white", fontWeight: 900, fontSize: 18 }}>T</span>
            </div>
            <span style={{ fontWeight: 900, fontSize: 20, color: textPri, letterSpacing: -0.3 }}>Tax Genius</span>
          </div>

          {/* Desktop nav */}
          <nav style={{ display: "flex", alignItems: "center", gap: 4 }} className="hidden md:flex">
            {navItems.map(item => (
              <button
                key={item.id}
                data-testid={`${item.id}-nav-btn`}
                onClick={item.action}
                style={{
                  display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 10,
                  border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700,
                  background: activeNav === item.id ? (D ? "#1a3a2a" : "#dcfce7") : "transparent",
                  color: activeNav === item.id ? accentGreen : textSec,
                  position: "relative", transition: "background 0.2s, color 0.2s",
                  fontFamily: "'Nunito', sans-serif",
                }}
                onMouseEnter={e => { if (activeNav !== item.id) e.currentTarget.style.background = navHov; }}
                onMouseLeave={e => { if (activeNav !== item.id) e.currentTarget.style.background = "transparent"; }}
              >
                {item.icon}
                {item.label}
                {item.badge > 0 && (
                  <span style={{ position: "absolute", top: 2, right: 2, background: "#ef4444", color: "white", fontSize: 10, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Right controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Dark toggle */}
            <button
              onClick={() => setDark(!D)}
              style={{ width: 38, height: 38, borderRadius: "50%", background: D ? "#334155" : "#f1f5f9", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {D ? <Sun size={17} color="#fbbf24" /> : <Moon size={17} color="#64748b" />}
            </button>
            {/* Logout */}
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              style={{ width: 38, height: 38, borderRadius: "50%", background: D ? "#334155" : "#f1f5f9", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <LogOut size={16} color={textSec} />
            </button>
          </div>
        </div>

        {/* Mobile nav bar */}
        <div style={{ borderTop: `1px solid ${border}`, display: "flex", padding: "8px 16px", gap: 4, overflowX: "auto" }} className="md:hidden">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={item.action}
              style={{
                flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 10,
                border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700,
                background: activeNav === item.id ? (D ? "#1a3a2a" : "#dcfce7") : "transparent",
                color: activeNav === item.id ? accentGreen : textSec,
                position: "relative", fontFamily: "'Nunito', sans-serif",
              }}
            >
              {item.icon} {item.label}
              {item.badge > 0 && (
                <span style={{ marginLeft: 4, background: "#ef4444", color: "white", fontSize: 10, width: 16, height: 16, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main style={{ maxWidth: 960, margin: "0 auto", padding: "28px 20px 60px" }}>

        {/* ── Row 1: Greeting + Level pill ── */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
          <div>
            <p style={{ color: textSec, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
            </p>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: textPri, letterSpacing: -0.4, margin: 0 }}>
              Welcome back, {user?.name?.split(" ")[0]} 👋
            </h1>
          </div>
          {/* Level + GP progress pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: surface, border: `1px solid ${border}`, borderRadius: 14, padding: "10px 18px" }}>
            <span style={{ fontSize: 22 }}>{level.emoji}</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: level.color }}>{level.name}</div>
              {nextLevel && <>
                <div style={{ fontSize: 10, color: textSec, marginBottom: 4 }}>{totalGP.toLocaleString()} / {nextLevel.min.toLocaleString()} GP</div>
                <div style={{ width: 100, height: 4, background: D ? "#334155" : "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg,#10b981,#059669)", borderRadius: 4, transition: "width 0.8s ease" }} />
                </div>
              </>}
            </div>
          </div>
        </motion.div>

        {/* ── Row 2: 4 stat pills ── */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
          style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 24 }}
          className="stats-grid"
        >
          {[
            { label: "Genius Points", value: loading ? "…" : totalGP.toLocaleString(), icon: <Zap size={15} />,      accent: "#f59e0b" },
            { label: "Quizzes Done",  value: loading ? "…" : (stats?.quizzes_taken || 0), icon: <BookOpen size={15} />, accent: "#6366f1" },
            { label: "Accuracy",      value: loading ? "…" : `${stats?.accuracy || 0}%`,  icon: <Target size={15} />,   accent: "#10b981" },
            { label: "Global Rank",   value: loading ? "…" : `#${stats?.rank || "—"}`,     icon: <Trophy size={15} />,   accent: "#e11d48" },
          ].map((s, i) => (
            <div key={i} style={{ background: surface, border: `1px solid ${border}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: s.accent + "18", display: "flex", alignItems: "center", justifyContent: "center", color: s.accent, flexShrink: 0 }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: 10, color: textSec, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: textPri, letterSpacing: -0.3 }}>{s.value}</div>
                {i === 0 && (
                  <div style={{ fontSize: 9, color: "#ef4444", fontWeight: 600, marginTop: 2 }}>
                    🚫 Repeat questions = 0 GP
                  </div>
                )}
              </div>
            </div>
          ))}
        </motion.div>

        {/* ── Row 3: Quiz CTA + Sidebar ── */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16, alignItems: "start" }}
          className="dash-grid"
        >

          {/* ── Left: Quiz card ── */}
          <div style={{
            background: "linear-gradient(135deg,#059669 0%,#0d9488 60%,#0284c7 100%)",
            borderRadius: 22, padding: "32px 36px", position: "relative", overflow: "hidden",
            boxShadow: `0 16px 48px rgba(5,150,105,${D ? "0.35" : "0.2"})`,
          }}>
            {/* decorative circles */}
            <div style={{ position: "absolute", width: 260, height: 260, background: "rgba(255,255,255,0.05)", borderRadius: "50%", top: -80, right: -70, pointerEvents: "none" }} />
            <div style={{ position: "absolute", width: 140, height: 140, background: "rgba(255,255,255,0.04)", borderRadius: "50%", bottom: -40, left: -30, pointerEvents: "none" }} />

            <div style={{ position: "relative" }}>
              {/* Headline */}
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)", marginBottom: 10 }}>
                🎯 Start Your Tax Challenge
              </div>
              <h2 style={{ fontSize: 26, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.15, margin: "0 0 24px", color: "#fff" }}>
                Ready to prove your<br />tax expertise?
              </h2>

              {/* Topic selector */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)", marginBottom: 8 }}>
                  Select Topic
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    value={selectedTopicId}
                    onChange={e => setSelectedTopicId(e.target.value)}
                    style={{
                      width: "100%", appearance: "none", WebkitAppearance: "none",
                      background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
                      border: "1.5px solid rgba(255,255,255,0.3)",
                      borderRadius: 12, padding: "12px 40px 12px 16px",
                      color: "#fff", fontSize: 14, fontWeight: 700,
                      cursor: "pointer", outline: "none",
                      fontFamily: "'Nunito', sans-serif",
                    }}
                  >
                    {QUIZ_TOPICS.map(t => (
                      <option
                        key={t.id}
                        value={t.id}
                        disabled={!t.active}
                        style={{ background: "#1e293b", color: t.active ? "#fff" : "#64748b" }}
                      >
                        {t.emoji}  {t.label}{!t.active ? " — Coming Soon" : ` · ${t.questions?.toLocaleString()} Qs`}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} color="rgba(255,255,255,0.7)" style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                </div>
              </div>

              {/* Quiz meta + CTA row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                  {[
                    { icon: <Clock size={12} />,   t: "~5 min" },
                    { icon: <Target size={12} />,  t: "15 questions" },
                    { icon: <Zap size={12} />,     t: "3 lifelines" },
                  ].map((x, i) => (
                    <span key={i} style={{ display: "flex", alignItems: "center", gap: 5, color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 600 }}>
                      {x.icon}{x.t}
                    </span>
                  ))}
                </div>

                <button
                  data-testid="start-quiz-btn"
                  onClick={() => {
                    if (selectedTopic?.active) handleOpenDifficultyModal();
                  }}
                  disabled={!selectedTopic?.active}
                  style={{
                    background: "#fff", color: "#059669", border: "none", borderRadius: 50,
                    padding: "14px 30px", fontWeight: 900, fontSize: 15, cursor: selectedTopic?.active ? "pointer" : "not-allowed",
                    display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
                    fontFamily: "'Nunito', sans-serif", flexShrink: 0,
                    boxShadow: "0 6px 24px rgba(0,0,0,0.16)", transition: "transform 0.15s",
                    opacity: selectedTopic?.active ? 1 : 0.6,
                  }}
                  onMouseEnter={e => { if (selectedTopic?.active) e.currentTarget.style.transform = "scale(1.05)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
                >
                  <Play size={17} fill="#059669" />
                  {selectedTopic?.active ? "Start Quiz" : "Coming Soon"}
                </button>
              </div>
            </div>
          </div>

          {/* ── Right sidebar ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {/* 1v1 Battle */}
            <div style={{ background: "linear-gradient(135deg,#7c3aed,#db2777)", borderRadius: 18, padding: "20px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", width: 100, height: 100, background: "rgba(255,255,255,0.06)", borderRadius: "50%", top: -20, right: -20 }} />
              <div style={{ position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <Swords size={16} color="white" />
                  <span style={{ color: "white", fontWeight: 900, fontSize: 15 }}>1v1 Battle</span>
                  {pendingChallenges > 0 && (
                    <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 100, padding: "2px 8px", fontSize: 10, fontWeight: 800, color: "white" }}>
                      {pendingChallenges}
                    </span>
                  )}
                </div>
                <p style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, marginBottom: 14 }}>Challenge a friend. Winner earns 150 GP!</p>
                <button
                  data-testid="challenge-friend-btn"
                  onClick={() => navigate("/challenges")}
                  style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.28)", borderRadius: 50, padding: "8px 18px", color: "white", fontWeight: 800, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'Nunito', sans-serif" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
                >
                  <Swords size={13} /> Challenge
                </button>
              </div>
            </div>

            {/* Leaderboard shortcut */}
            <button onClick={() => navigate("/leaderboard")}
              style={{ background: surface, border: `1px solid ${border}`, borderRadius: 14, padding: "13px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", fontFamily: "'Nunito', sans-serif", transition: "background 0.15s, border-color 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = surface2; e.currentTarget.style.borderColor = "#f59e0b55"; }}
              onMouseLeave={e => { e.currentTarget.style.background = surface;  e.currentTarget.style.borderColor = border; }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "#f59e0b18", color: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Trophy size={16} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: textPri }}>Leaderboard</div>
                <div style={{ fontSize: 11, color: textSec }}>See top earners</div>
              </div>
              <ChevronRight size={14} color={textSec} />
            </button>

            {/* Upcoming topics teaser */}
            <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 14, padding: "16px" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: textSec, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>
                Coming Soon
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {QUIZ_TOPICS.filter(t => !t.active).map(t => (
                  <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.55 }}>
                    <span style={{ fontSize: 18 }}>{t.emoji}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: textPri, flex: 1 }}>{t.label}</span>
                    <Lock size={11} color={textSec} />
                  </div>
                ))}
              </div>
            </div>

          </div>
        </motion.div>

      </main>

      <DifficultyModal
        isOpen={showDifficultyModal}
        onClose={() => setShowDifficultyModal(false)}
        onSelect={handleStartQuiz}
        difficultyStatus={difficultyStatus}
      />

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @media (max-width: 860px) { .dash-grid  { grid-template-columns: 1fr !important; } }
        @media (max-width: 600px) { .stats-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 380px) { .stats-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
};

export default Dashboard;
